import { Button, Col, Row, Space, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Show } from '@refinedev/antd'
import { useOne } from '@refinedev/core'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { SettlementRoot2 } from '@repo/sdk'

import { DetailNotFound } from '../../components/DetailNotFound'
import { PageError } from '../../components/PageError'
import { StatusBadge } from '../../components/StatusBadge'
import { useBackToList } from '../../shared/hooks/useBackToList'
import { useSettlementStatusConfig } from './settlementStatusConfig'
import { SettlementInfoCard } from './SettlementInfoCard'
import { SettlementOffsetsCard } from './SettlementOffsetsCard'
import { SettlementOrderInfoCard } from './SettlementOrderInfoCard'
import { SettlementTimelineCard } from './SettlementTimelineCard'

export function SettlementDetail() {
  const { id } = useParams<{ id: string }>()
  const backToList = useBackToList('/settlements/history')
  const { t } = useTranslation()
  const statusConfig = useSettlementStatusConfig()

  const { query } = useOne<SettlementRoot2>({
    resource: 'settlement-history-list',
    id: id ?? '',
    queryOptions: { enabled: !!id },
  })

  const { data, isLoading, isError } = query
  const settlement = data?.data

  if (!isLoading && isError) return <PageError onRetry={() => void query.refetch()} />
  if (!isLoading && !isError && !settlement) return <DetailNotFound backTo="/settlements/history" />

  return (
    <Show
      isLoading={isLoading}
      title={t('pages.settlements.detailTitle')}
      headerButtons={<Space />}
    >
      <Button
        data-testid="settlement-detail-back-btn"
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={backToList}
        style={{ padding: 0, marginBottom: 16 }}
      >
        {t('common.buttons.backToList')}
      </Button>

      {settlement && (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {settlement.settlementId.slice(0, 8)}...
            </Typography.Title>
            <StatusBadge status={settlement.status} configMap={statusConfig} />
          </Space>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <SettlementInfoCard settlement={settlement} />
            </Col>
            <Col xs={24} lg={12}>
              <SettlementOrderInfoCard settlement={settlement} />
            </Col>
            {settlement.offsets && settlement.offsets.length > 0 && (
              <Col xs={24}>
                <SettlementOffsetsCard settlement={settlement} />
              </Col>
            )}
            <Col xs={24}>
              <SettlementTimelineCard settlement={settlement} />
            </Col>
          </Row>
        </>
      )}
    </Show>
  )
}
